import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, LoaderCircle, TriangleAlert } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EASE } from "@/components/landing/Reveal";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const { setUser } = useAuth();
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          const { data: me } = await api.get("/auth/me");
          setUser(me);
          setState("success");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
      } catch { /* retry */ }
      if (attempts.current >= 12) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4" data-testid="payment-success-page">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}
        className="card-integra w-full max-w-md p-8 text-center">
        {state === "checking" && (
          <div data-testid="payment-checking">
            <LoaderCircle className="mx-auto h-10 w-10 animate-spin text-bleu" />
            <h1 className="mt-5 font-display text-xl font-extrabold text-slate-900">Vérification du paiement…</h1>
            <p className="mt-2 text-sm text-slate-500">Un instant, nous confirmons votre abonnement auprès de Stripe.</p>
          </div>
        )}
        {state === "success" && (
          <div data-testid="payment-confirmed">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
              <Crown className="h-8 w-8 text-amber-600" />
            </span>
            <h1 className="mt-5 font-display text-xl font-extrabold text-slate-900">Bienvenue en Premium !</h1>
            <p className="mt-2 text-sm leading-relaxed text-slate-500">
              Votre abonnement à 7,99 €/mois est actif. IA illimitée, examens blancs illimités et bien plus.
            </p>
            <Link to="/dashboard" className="btn-primary mx-auto mt-6 !px-8" data-testid="payment-success-dashboard-link">
              Accéder à mon espace
            </Link>
          </div>
        )}
        {(state === "timeout" || state === "error") && (
          <div data-testid="payment-issue">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-rose-50">
              <TriangleAlert className="h-8 w-8 text-rouge" />
            </span>
            <h1 className="mt-5 font-display text-xl font-extrabold text-slate-900">
              {state === "timeout" ? "Confirmation en attente" : "Paiement non confirmé"}
            </h1>
            <p className="mt-2 text-sm leading-relaxed text-slate-500">
              {state === "timeout"
                ? "Votre paiement est en cours de traitement. Votre compte sera mis à niveau automatiquement dès confirmation."
                : "Le paiement n'a pas pu être confirmé. Aucun montant n'a été prélevé si la transaction a échoué."}
            </p>
            <Link to="/dashboard/parametres" className="btn-secondary mx-auto mt-6 !px-8" data-testid="payment-issue-back-link">
              Retour aux paramètres
            </Link>
          </div>
        )}
      </motion.div>
    </div>
  );
}
